import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import SocialMediaPlan from '../../components/SocialMediaPlan';
import FullItineraryBuilder from '../../components/FullItineraryBuilder';
import SkeletalItineraryView from '../../components/SkeletalItineraryView';

function formatDate(str) {
  if (!str) return '—';
  const [y, m, d] = str.split('-');
  return `${d}/${m}/${y}`;
}

const TOUR_TABS = [
  { key: 'social',   label: 'Social Media Plan' },
  { key: 'full',     label: 'Full Itinerary' },
  { key: 'skeletal', label: 'Skeletal Itinerary' },
];

export default function MarketingDashboard() {
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();
  const [tours, setTours] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [activeTab, setActiveTab] = useState('social');

  useEffect(() => {
    if (!currentUser) return;
    const q = query(collection(db, 'tourSummaries'), where('marketingId', '==', currentUser.uid));
    const unsub = onSnapshot(q, (snap) => {
      const docs = snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .sort((a, b) => (a.departureDate || '').localeCompare(b.departureDate || ''));
      setTours(docs);
      setLoading(false);
    });
    return unsub;
  }, [currentUser]);

  async function handleLogout() {
    await logout();
    navigate('/login');
  }

  const initials = currentUser?.email?.[0]?.toUpperCase() || '?';
  const displayName = currentUser?.displayName || currentUser?.email || '';

  return (
    <div className="min-h-screen bg-page flex flex-col">
      <header className="h-14 bg-navy flex items-center justify-between px-4 flex-shrink-0 z-20">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-[8px] bg-ba flex items-center justify-center flex-shrink-0">
            <span className="text-white font-bold text-[12px] tracking-wide">MST</span>
          </div>
          <span className="hidden sm:inline text-white font-semibold text-[15px]">Marketing Portal</span>
          <span className="bg-white/15 text-white text-[11px] px-3 py-0.5 rounded-[20px]">Marketing</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-2 bg-white/10 rounded-[20px] px-3 py-1.5">
            <span className="w-6 h-6 rounded-full bg-bt flex items-center justify-center text-[11px] font-semibold text-white flex-shrink-0">
              {initials}
            </span>
            <span className="hidden sm:inline text-white text-[13px] max-w-[140px] truncate">{displayName}</span>
          </div>
          <button onClick={handleLogout} className="text-white/70 hover:text-white text-[12px] transition px-1">
            Sign out
          </button>
        </div>
      </header>

      <main className="flex-1 px-4 py-5 md:px-8 md:py-8 overflow-y-auto max-w-3xl mx-auto w-full page-fade">
        <div className="mb-4">
          <h2 className="text-[20px] font-bold text-[#111827]">Your Tours</h2>
          <p className="text-[13px] text-[#6B7280] mt-0.5">
            Upload the social media plan and content notes for each tour so the tour leader knows what to capture.
          </p>
        </div>

        {loading ? (
          <p className="text-[13px] text-[#9CA3AF]">Loading…</p>
        ) : tours.length === 0 ? (
          <div className="bg-white rounded-[12px] border border-[#E5E7EB] p-10 text-center">
            <p className="text-[#9CA3AF] text-[13px]">No tours assigned to you yet — MST staff will assign one when it's ready.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {tours.map((tour) => {
              const isOpen = expanded === tour.id;
              return (
                <div key={tour.id} className="bg-white rounded-[12px] border border-[#E5E7EB] overflow-hidden">
                  <button
                    onClick={() => { setExpanded(isOpen ? null : tour.id); setActiveTab('social'); }}
                    className="w-full text-left flex items-center justify-between gap-4 p-5 hover:bg-surface transition"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-[14px] text-[#111827]">{tour.tourName || 'Untitled tour'}</p>
                      <p className="text-[13px] text-[#6B7280] mt-0.5">
                        {formatDate(tour.departureDate)} → {formatDate(tour.returnDate)}
                      </p>
                    </div>
                    <svg
                      width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2"
                      strokeLinecap="round" strokeLinejoin="round"
                      className={`flex-shrink-0 transition-transform ${isOpen ? 'rotate-90' : ''}`}
                    >
                      <path d="M9 18l6-6-6-6" />
                    </svg>
                  </button>
                  {isOpen && (
                    <div className="border-t border-[#E5E7EB]">
                      <div className="flex gap-1 px-5 pt-4 border-b border-[#F1F3F6]">
                        {TOUR_TABS.map((tab) => (
                          <button
                            key={tab.key}
                            onClick={() => setActiveTab(tab.key)}
                            className={`px-3 py-2 text-[13px] font-semibold rounded-t-[8px] border-b-2 transition ${
                              activeTab === tab.key
                                ? 'text-navy border-navy'
                                : 'text-[#6B7280] border-transparent hover:text-[#374151]'
                            }`}
                          >
                            {tab.label}
                          </button>
                        ))}
                      </div>
                      <div className="p-5">
                        {activeTab === 'social' && (
                          <SocialMediaPlan requestId={tour.id} role="marketing" />
                        )}
                        {activeTab === 'full' && (
                          <FullItineraryBuilder
                            requestId={tour.id}
                            request={{ departureDate: tour.departureDate, returnDate: tour.returnDate }}
                            readOnly
                            restricted
                          />
                        )}
                        {activeTab === 'skeletal' && (
                          <SkeletalItineraryView
                            requestId={tour.id}
                            departureDate={tour.departureDate}
                            returnDate={tour.returnDate}
                          />
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}
